import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { Field } from "../components/fields";
import DateInput from "../components/DateInput";

const EMPTY = {
  subject_name: "", subject_id: "", operation_desc: "", amount: "", currency: "USD",
  channel: "", detection_date: new Date().toISOString().slice(0, 10), unusual_reason: "",
  examen_notes: "", examen_date: "", decision: "pendiente", decision_date: "", uaf_reference: "",
};

export default function Ros() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => api.get("/compliance/ros", { params: showInactive ? { include_inactive: "1" } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [showInactive]);

  const toggleActive = async (r) => {
    try {
      await api.patch(`/compliance/ros/${r.id}`, { data: {}, active: !(r.active !== false) });
      toast.success(t("ros.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/compliance/ros/${editing}`, { data: form });
        toast.success(t("ros.updated"));
      } else {
        await api.post("/compliance/ros", { data: form });
        toast.success(t("ros.saved"));
      }
      setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (r) => { setForm({ ...EMPTY, ...r.data }); setEditing(r.id); setShowForm(true); };
  const statusCls = { en_analisis: "bg-amber-50 text-amber-700", reportado_uaf: "bg-red-50 text-red-700", archivado: "bg-slate-100 text-slate-600" };

  return (
    <div className="space-y-6" data-testid="ros-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("ros.title")}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">{t("ros.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadFile(`/export/compliance.pdf?kind=ros&lang=${lang}`, "ros_kycfederal.pdf")} data-testid="ros-export-pdf" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold hover:bg-[#16335F]">
            <Download className="w-4 h-4" /> {t("common.export_pdf")}
          </button>
          <button onClick={() => downloadFile("/export/compliance.xlsx?kind=ros", "ros_kycfederal.xlsx")} data-testid="ros-export-excel" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800">
            <FileSpreadsheet className="w-4 h-4" /> {t("common.download_excel")}
          </button>
          <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(EMPTY); }} data-testid="ros-add-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C]">
            <Plus className="w-4 h-4" /> {t("ros.new")}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="ec-card p-6 space-y-4" data-testid="ros-form">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label={t("ros.subject_name")}><input className="ec-input" value={form.subject_name} onChange={set("subject_name")} required data-testid="ros-subject-name" /></Field>
            <Field label={t("ros.subject_id")}><input className="ec-input" value={form.subject_id} onChange={set("subject_id")} data-testid="ros-subject-id" /></Field>
            <Field label={t("ros.detection_date")}><DateInput className="ec-input" value={form.detection_date} onChange={set("detection_date")} data-testid="ros-detection-date" /></Field>
            <Field label={t("ros.amount")}><input className="ec-input" type="number" step="0.01" value={form.amount} onChange={set("amount")} data-testid="ros-amount" /></Field>
            <Field label={t("ros.currency")}>
              <select className="ec-input" value={form.currency} onChange={set("currency")} data-testid="ros-currency"><option>USD</option><option>EUR</option><option>PAB</option></select>
            </Field>
            <Field label={t("ros.channel")}><input className="ec-input" value={form.channel} onChange={set("channel")} data-testid="ros-channel" /></Field>
          </div>
          <Field label={t("ros.operation_desc")}><textarea className="ec-input" rows={2} value={form.operation_desc} onChange={set("operation_desc")} data-testid="ros-operation-desc" /></Field>
          <Field label={t("ros.unusual_reason")}><textarea className="ec-input" rows={2} value={form.unusual_reason} onChange={set("unusual_reason")} data-testid="ros-unusual-reason" /></Field>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-3 border-t border-slate-100">
            <Field label={t("ros.examen_date")}><DateInput className="ec-input" value={form.examen_date} onChange={set("examen_date")} data-testid="ros-examen-date" /></Field>
            <Field label={t("ros.decision")}>
              <select className="ec-input" value={form.decision} onChange={set("decision")} data-testid="ros-decision">
                <option value="pendiente">{t("ros.d_pending")}</option>
                <option value="reportar_uaf">{t("ros.d_report")}</option>
                <option value="archivar">{t("ros.d_archive")}</option>
              </select>
            </Field>
            <Field label={t("ros.decision_date")}><DateInput className="ec-input" value={form.decision_date} onChange={set("decision_date")} data-testid="ros-decision-date" /></Field>
            <Field label={t("ros.uaf_reference")}><input className="ec-input" value={form.uaf_reference} onChange={set("uaf_reference")} data-testid="ros-uaf-reference" /></Field>
          </div>
          <Field label={t("ros.examen_notes")}><textarea className="ec-input" rows={2} value={form.examen_notes} onChange={set("examen_notes")} data-testid="ros-examen-notes" /></Field>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} data-testid="ros-submit" className="px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
            </button>
          </div>
        </form>
      )}

      <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="ros-show-inactive" />
        {t("common.show_inactive")}
      </label>

      <div className="ec-card overflow-hidden" data-testid="ros-table">
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="ros-empty">{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.number")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("ros.subject_name")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("ros.amount")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("ros.status")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.date")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ros-row-${r.report_number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{r.report_number}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{r.data?.subject_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.data?.amount} {r.data?.currency}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusCls[r.status]}`} data-testid={`ros-status-${r.report_number}`}>{t(`ros.st_${r.status}`)}</span>
                      {r.active === false && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">{t("common.inactive")}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-right space-x-3">
                      <button onClick={() => toggleActive(r)} data-testid={`ros-toggle-${r.report_number}`} className={`text-xs font-bold ${r.active !== false ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}>{r.active !== false ? t("common.deactivate") : t("common.activate")}</button>
                      <button onClick={() => startEdit(r)} data-testid={`ros-edit-${r.report_number}`} className="text-slate-500 hover:text-slate-800 text-xs font-bold inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />{t("ros.edit")}</button>
                      <button onClick={() => downloadFile(`/compliance/ros/${r.id}/pdf?lang=${lang}`, `${r.report_number}.pdf`)} data-testid={`ros-pdf-${r.report_number}`} className="text-[#044CBC] hover:text-[#033A8C] text-xs font-bold inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" />PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
